import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { paintBuildingAt } from "../../lib/engine";
import { C, FONT_HEAD, FONT_BODY } from "./theme";

// Thumbnail lukisan asli bangunan untuk kartu katalog
export function Thumb({ def }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !def) return;
    const S = 132;
    cv.width = S; cv.height = S;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, S, S);
    const zoom = def.category === "Karakter" ? 4.7 : def.category === "Kendaraan" ? 1.85 : Math.min(0.85, 92 / ((def.w + def.h) * 32));
    try { paintBuildingAt(ctx, def, S / 2, S * 0.78, zoom); } catch { /* abaikan */ }
  }, [def]);
  return <canvas ref={ref} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// Pemilih varian visual — pratinjau lukisan asli tiap varian (Dasar + semua varian)
export function VariantPicker({ def, value, onChange, size = 64 }) {
  const options = [{ vid: "", name: "Dasar", paintCode: def.paintCode }, ...(def.variants || [])];
  return (
    <div data-testid="variant-picker" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
      {options.map(v => {
        const active = (value || "") === v.vid;
        return (
          <button key={v.vid || "base"} data-testid={`variant-${v.vid || "base"}`} onClick={() => onChange(v.vid || null)}
            title={`Pakai ${v.name}`}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: 4, cursor: "pointer",
              background: active ? C.blueSoft : "#fff", border: `1.5px solid ${active ? C.blue : C.line}`,
              borderRadius: 14, transition: "border-color 0.15s, box-shadow 0.15s",
              boxShadow: active ? `0 0 0 2px ${C.blueSoft}` : "none"
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = "#b9b6a6"; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = C.line; }}>
            <span style={{ display: "block", width: size, height: size, borderRadius: 10, overflow: "hidden", background: "linear-gradient(180deg,#eef4fa,#e4edf6)" }}>
              <Thumb def={{ ...def, paintCode: v.paintCode }} />
            </span>
            <span style={{ fontSize: 9.5, fontWeight: 700, color: active ? C.blue : C.sub, fontFamily: FONT_BODY, maxWidth: size, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {v.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}


// Accordion kategori katalog
export function Category({ name, items, tool, onPick, defaultOpen, forceOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]); // pencarian otomatis membuka grup
  return (
    <div data-testid={`catalog-category-${name.toLowerCase()}`} style={{ borderBottom: `1px solid ${C.line}` }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", background: "transparent", border: 0, color: C.sub, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: FONT_BODY }}>
        <span>{name} <span style={{ color: C.t3, fontWeight: 500 }}>({items.length})</span></span>
        <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
      </button>
      {open && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "2px 16px 16px" }}>
          {items.map(d => (
            <button key={d.bid} data-testid={`tool-${d.bid}`} onClick={() => onPick(d)}
              style={{
                position: "relative", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden",
                background: "#fff", border: `1.5px solid ${tool?.bid === d.bid ? C.blue : C.line}`,
                borderRadius: 16, cursor: "pointer", textAlign: "left", transition: "border-color 0.15s, box-shadow 0.15s",
                boxShadow: tool?.bid === d.bid ? `0 0 0 2px ${C.blueSoft}` : "0 4px 12px rgba(20,20,20,0.05)"
              }}
              onMouseEnter={e => { if (tool?.bid !== d.bid) e.currentTarget.style.borderColor = "#b9b6a6"; }}
              onMouseLeave={e => { if (tool?.bid !== d.bid) e.currentTarget.style.borderColor = C.line; }}>
              <span style={{ position: "absolute", top: 7, right: 7, zIndex: 2, background: "#fff", color: C.sub, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, border: `1px solid ${C.line}` }}>
                {d.w}x{d.h}
              </span>
              <span style={{ display: "block", aspectRatio: "1", background: "linear-gradient(180deg,#eef4fa,#e4edf6)" }}>
                <Thumb def={d} />
              </span>
              <span style={{ padding: "8px 10px 10px", fontSize: 12, fontWeight: 700, color: C.ink, fontFamily: FONT_BODY, lineHeight: 1.25 }}>
                {d.name}
                <span style={{ display: "block", fontSize: 10, color: C.t3, fontWeight: 500 }}>{d.floors} lantai</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// CityEditor — editor kota bersama (dipakai walikota & admin), tema terang ala Dashboard.
// Props: title (nama kota), loadState: () => Promise<state>, saveState: (state) => Promise, onLogout (opsional), headerExtra (opsional node di header)
